import axios from "axios";
import { AXIOS_CONFIG } from "@/utils/api";
import { backend_url } from "@/constants";
import { Endpoints } from "@api/endpoints.ts";

export interface TaxOfficeData {
  ptin?: string;
  efin?: string;
  ein?: string;
  firmName?: string;
  firmAddress?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  clientPin?: string;
  bankProducts?: string;
  softwareType?: string;
  intakeSoftware?: string;
  totalEfiles?: number;
  businessType?: string;
}

/**
 * Fetches the tax office information from the backend API.
 * This function retrieves the tax professional's office details including PTIN, EFIN, EIN,
 * firm information, banking details, and software type.
 *
 * @returns {Promise<TaxOfficeData | null>} A promise that resolves to the tax office data if successful,
 *                                         or null if no data is found
 * @throws {Error} If the API request fails
 */
export const fetchTaxOfficeData = async (): Promise<TaxOfficeData | null> => {
  try {
    const response = await axios.get(
      `${backend_url}/api/onboarding/taxoffice/`,
      {
        ...AXIOS_CONFIG,
      }
    );

    return response.data.content || null;
  } catch (error) {
    console.error("Failed to load tax office information", error);
    throw new Error("Failed to load tax office information");
  }
};

/**
 * Submits tax office information to the backend API.
 * This function sends the tax professional's office details for storage or update,
 * including all required fields like PTIN, EFIN, EIN, firm information, and software type.
 * All fields are optional, allowing for partial updates.
 *
 * @param {TaxOfficeData} data - The tax office information to be submitted
 * @returns {Promise<void>} A promise that resolves when the submission is successful
 * @throws {Error} If the API request fails, with a descriptive error message
 */
export const submitTaxOfficeData = async (
  data: TaxOfficeData
): Promise<void> => {
  try {
    await axios.post(`${backend_url}/api/onboarding/taxoffice/`, data, {
      ...AXIOS_CONFIG,
    });
  } catch (error) {
    throw new Error(
      error instanceof Error
        ? error.message
        : "Failed to submit tax information. Please try again."
    );
  }
};

export interface SampleClientResponse {
  clients: SampleClientData[];
}

export interface SampleClientData {
  clientId: string;
  clientEmail: string;
  clientName: string;
  clientSSN: string;
}

/**
 * Creates a sample client for lite onboarding by copying from a template client.
 * This function creates a sample taxpayer with pre-populated documents and OCR data
 * for demonstration purposes during the onboarding process.
 *
 * @returns {Promise<SampleClientResponse>} A promise that resolves to the created sample client data
 * @throws {Error} If the API request fails or client creation encounters an error
 */
export const createSampleClient = async (): Promise<SampleClientResponse> => {
  try {
    const response = await axios.post(
      `${backend_url}/api/onboarding/create-sample-client/`,
      {},
      {
        ...AXIOS_CONFIG,
      }
    );

    return response.data.content;
  } catch (error) {
    console.error("Failed to create sample client", error);

    // Handle axios errors and relay backend message
    if (axios.isAxiosError(error) && error.response) {
      const backendMessage = error.response.data?.message;
      if (backendMessage) {
        throw new Error(backendMessage);
      }
    }

    // Handle non-axios errors
    throw new Error(
      error instanceof Error
        ? error.message
        : "Failed to create sample client. Please try again."
    );
  }
};

/**
 * Marks the lite onboarding as complete.
 */
export const markLiteOnboardingComplete = async () => {
  try {
    await axios.post(
      `${backend_url}/${Endpoints.onboarding.liteComplete}`,
      {},
      AXIOS_CONFIG
    );
    console.log("Lite onboarding marked as completed");
  } catch (error) {
    console.error("Failed to mark lite onboarding as completed:", error);
    // Continue even if API call fails
  }
};

/**
 * Marks the regular (CRM) onboarding as complete.
 */
export const markOnboardingComplete = async () => {
  try {
    await axios.post(
      `${backend_url}/${Endpoints.onboarding.complete}`,
      {},
      AXIOS_CONFIG
    );
    console.log("Regular onboarding marked as completed");
  } catch (error) {
    console.error("Failed to mark regular onboarding as completed:", error);
    // Continue even if API call fails
  }
};

/**
 * Marks both lite and regular onboarding as complete.
 * This is used when completing the unified onboarding flow.
 */
export const markAllOnboardingComplete = async () => {
  try {
    await Promise.all([markLiteOnboardingComplete(), markOnboardingComplete()]);
    console.log("All onboarding types marked as completed");
  } catch (error) {
    console.error("Failed to mark all onboarding as completed:", error);
    // Continue even if API calls fail
  }
};
